import React, { useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, apiFetch } from '../api';
import { useAuth } from '../auth';
import { useToast } from '../toast';
import { AuthLayout } from '../components/AuthLayout';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const messages = {
  default: '喵~ 欢迎回来！',
  typing: '喵喵~ 在输入什么呢？',
  password: '喵...密码要保密哦！',
  code: '喵！验证码发到邮箱啦~',
  empty: '喵？还没填完呢~',
  success: '喵呜~ 登录成功！',
  peeking: '喵！我没偷看！',
  notPeeking: '喵~ 我什么都没看到！'
};

export function LoginPage({ forcedNext }: { forcedNext?: string }) {
  const { login, loginByCode } = useAuth();
  const toast = useToast();
  const nav = useNavigate();
  const location = useLocation();
  const next = useMemo(() => {
    if (forcedNext) return forcedNext;
    const q = new URLSearchParams(location.search);
    return q.get('next') || '/';
  }, [location.search, forcedNext]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState<'password' | 'code'>('password');
  const [cooldown, setCooldown] = useState(0);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const sendPendingRef = useRef(false);
  const [catMessage, setCatMessage] = useState(messages.default);
  const [isPeeking, setIsPeeking] = useState(false);
  const [isCoveringEyes, setIsCoveringEyes] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const normalizedEmail = email.trim();
  const emailValid = normalizedEmail.length > 0 && EMAIL_RE.test(normalizedEmail);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [cooldown]);

  const ensureRegisteredEmail = async () => {
    if (!emailValid) {
      toast.error('邮箱格式不正确');
      return false;
    }
    setEmailCheckLoading(true);
    try {
      const res = await apiFetch<{ ok: boolean; exists: boolean }>('/api/auth/check-email', {
        method: 'POST',
        body: JSON.stringify({ email: normalizedEmail })
      });
      if (!res.exists) {
        toast.error('该邮箱未注册，请先注册');
        return false;
      }
      return true;
    } finally {
      setEmailCheckLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailValid) {
      setCatMessage(messages.empty);
      toast.error('邮箱格式不正确');
      return;
    }
    if (loginMode === 'password' && !password) {
      setCatMessage(messages.empty);
      return;
    }
    if (loginMode === 'code' && code.length !== 6) {
      setCatMessage(messages.empty);
      toast.error('请输入 6 位验证码');
      return;
    }

    setLoading(true);
    setCatMessage('喵... 正在努力登录中...');

    try {
      if (loginMode === 'password') {
        await login(normalizedEmail, password);
      } else {
        await loginByCode(normalizedEmail, code);
      }
      setCatMessage(messages.success);
      toast.success('登录成功');
      nav(next, { replace: true });
    } catch (e) {
      setCatMessage('喵呜... 登录失败了...');
      if (e instanceof ApiError) {
        const code = (e.body as any)?.error;
        const retryAfter = Number((e.body as any)?.retryAfter || 0);
        const msg =
          e.status === 401
            ? loginMode === 'password'
              ? '邮箱或密码错误'
              : '邮箱或验证码错误'
            : e.status === 429 && code === 'LOCKED'
              ? `登录尝试过多，请 ${Math.max(1, retryAfter || 60)} 秒后再试`
              : code === 'CODE_NOT_SENT'
                ? '请先发送验证码'
                : code === 'CODE_EXPIRED'
                  ? '验证码已过期，请重新发送'
                  : code === 'CODE_INVALID'
                    ? '验证码错误'
                    : '登录失败，请稍后重试';
        toast.error(msg);
      } else {
        toast.error('登录失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    const newShowPassword = !showPassword;
    setShowPassword(newShowPassword);
    
    if (newShowPassword) {
      setIsCoveringEyes(true);
      setCatMessage(messages.peeking);
    } else {
      setIsCoveringEyes(false);
      setIsPeeking(false);
      setCatMessage(messages.notPeeking);
      setTimeout(() => {
        if (!isCoveringEyes) {
           setCatMessage(messages.default);
        }
      }, 1500);
    }
  };

  return (
    <AuthLayout catMessage={catMessage} isPeeking={isPeeking} isCoveringEyes={isCoveringEyes}>
      <h1 className="login-title">喵喵<span>登录</span></h1>
      <form onSubmit={handleLogin}>
        <div className="extra-links" style={{ marginTop: 0, marginBottom: '14px' }}>
          <button
            type="button"
            className="social-btn"
            style={{ width: 'auto', height: '36px', borderRadius: '999px', padding: '0 14px', fontSize: '13px' }}
            onClick={() => {
              setLoginMode('password');
              setCode('');
              setCatMessage(messages.default);
            }}
          >
            密码登录
          </button>
          <button
            type="button"
            className="social-btn"
            style={{ width: 'auto', height: '36px', borderRadius: '999px', padding: '0 14px', fontSize: '13px' }}
            onClick={() => {
              setLoginMode('code');
              setPassword('');
              setShowPassword(false);
              setIsCoveringEyes(false);
              setIsPeeking(false);
              setCatMessage(messages.code);
            }}
          >
            验证码登录
          </button>
        </div>
        <div className="form-group">
          <label>邮箱</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.replace(/\s+/g, '').slice(0, 254))}
            onFocus={() => setCatMessage(messages.typing)}
            onBlur={() => !isCoveringEyes && setCatMessage(messages.default)}
            placeholder="name@example.com"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
          />
          <span className="input-icon">🐱</span>
        </div>

        {loginMode === 'password' ? (
          <div className="form-group">
            <label>密码</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value.slice(0, 64))}
              onFocus={() => setCatMessage(messages.password)}
              onBlur={() => !isCoveringEyes && setCatMessage(messages.default)}
              placeholder="请输入密码"
              autoComplete="current-password"
              maxLength={64}
            />
            <span
              className="toggle-password"
              onClick={togglePasswordVisibility}
              onMouseEnter={() => !showPassword && setIsPeeking(true)}
              onMouseLeave={() => setIsPeeking(false)}
            >
              {showPassword ? '🙈' : '👁️'}
            </span>
            <span className="input-icon">🔒</span>
          </div>
        ) : (
          <div className="form-group">
            <label>验证码</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                  onFocus={() => setCatMessage(messages.code)}
                  onBlur={() => !isCoveringEyes && setCatMessage(messages.default)}
                  placeholder="6 位数字验证码"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                />
                <span className="input-icon" style={{ right: '10px' }}>🔢</span>
              </div>
              <button
                type="button"
                className="login-btn"
                style={{ width: 'auto', marginTop: 0, padding: '0 15px', fontSize: '14px', whiteSpace: 'nowrap' }}
                disabled={loading || cooldown > 0 || emailCheckLoading}
                onClick={async (evt) => {
                  evt.preventDefault();
                  if (sendPendingRef.current) return;
                  const ok = await ensureRegisteredEmail();
                  if (!ok) return;
                  sendPendingRef.current = true;
                  try {
                    const res = await apiFetch<{ ok: boolean; devCode?: string; devMode?: boolean }>('/api/auth/send-code', {
                      method: 'POST',
                      body: JSON.stringify({ email: normalizedEmail })
                    });
                    setCooldown(60);
                    if (res?.devMode && res?.devCode) {
                      toast.success(`开发模式验证码：${res.devCode}`);
                    } else {
                      toast.success('登录验证码已发送');
                    }
                    setCatMessage(messages.code);
                  } catch (err) {
                    if (err instanceof ApiError) {
                      const ecode = (err.body as any)?.error;
                      const retryAfter = Number((err.body as any)?.retryAfter || 0);
                      const msg =
                        ecode === 'TOO_MANY_REQUESTS'
                          ? `发送过于频繁，请 ${Math.max(1, retryAfter || 60)} 秒后重试`
                          : ecode === 'EMAIL_NOT_CONFIGURED'
                            ? '邮件服务未配置，请联系管理员'
                            : ecode === 'EMAIL_SMTP_CONFIG_INVALID'
                              ? '邮件服务配置异常，请联系管理员'
                              : ecode === 'EMAIL_AUTH_FAILED'
                                ? '邮件服务认证失败，请联系管理员'
                                : ecode === 'EMAIL_CONNECT_TIMEOUT'
                                  ? '邮件服务连接超时，请稍后重试'
                                  : ecode === 'EMAIL_SEND_FAILED'
                                    ? '邮件发送失败，请稍后重试'
                                    : '验证码发送失败，请稍后重试';
                      toast.error(msg);
                    } else {
                      toast.error('验证码发送失败，请稍后重试');
                    }
                  } finally {
                    sendPendingRef.current = false;
                  }
                }}
              >
                {cooldown > 0 ? `${cooldown}s` : '发送验证码'}
              </button>
            </div>
          </div>
        )}

        <button type="submit" className="login-btn" disabled={loading}>
          {loading ? '登录中...' : '登录'}
        </button>
      </form>

      <div className="extra-links">
        <Link to={`/reset-password?next=${encodeURIComponent(next)}&email=${encodeURIComponent(normalizedEmail)}`}>忘记密码？</Link>
        <Link to={`/register?next=${encodeURIComponent(next)}`}>注册账号</Link>
      </div>

      <div style={{ textAlign: 'center', marginTop: '20px' }}>
         <Link to="/" style={{ color: 'var(--cat-warm-brown)', fontSize: '14px', textDecoration: 'none' }}>返回首页</Link>
      </div>
    </AuthLayout>
  );
}
